// frontend/src/utils/tenant.js

// Tenant code mappings (for aliases)
const TENANT_MAPPINGS = {
  tooraflexonlinestore: "tooraflex-online",
  tooraflexonline: "tooraflex-online",
  tfonline: "tooraflex-online",
  localhost: "tooraflex-online",
  majestic: "majestic-healthwellness",
  "majestic-health": "majestic-healthwellness",
  lebaba: "lebaba-inware",
  "lebaba-inware": "lebaba-inware",
  terraflex: "terraflex",
  "tooraflex-online": "tooraflex-online",
};

// Map tenant code to correct database value
export const mapTenantCode = (tenantCode) => {
  if (!tenantCode) return "tooraflex-online";
  const mapped = TENANT_MAPPINGS[tenantCode] || tenantCode;
  console.log(`📍 Mapping tenant: ${tenantCode} -> ${mapped}`);
  return mapped;
};

export const getTenantFromUrl = () => {
  const hostname = window.location.hostname;
  console.log("getTenantFromUrl - hostname:", hostname);

  const urlParams = new URLSearchParams(window.location.search);
  const previewTenant = urlParams.get("tenant") || urlParams.get("tenantCode");
  if (urlParams.get("adminPreview") === "1" && previewTenant) {
    return mapTenantCode(previewTenant);
  }

  // Check for localhost subdomains (e.g., lebaba-inware.localhost)
  if (hostname.includes(".localhost")) {
    const parts = hostname.split(".");
    if (parts.length >= 2 && parts[0] !== "localhost" && parts[0] !== "www") {
      let tenant = parts[0];
      console.log("Found tenant from localhost subdomain:", tenant);
      return mapTenantCode(tenant);
    }
  }

  // Check if running on localhost without subdomain
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    const tenantParam = urlParams.get("tenant") || urlParams.get("tenantCode");
    if (tenantParam) {
      console.log("Found tenant from URL param:", tenantParam);
      return mapTenantCode(tenantParam);
    }
    console.log("No tenant found, using default: tooraflex-online");
    return "tooraflex-online";
  }

  // ✅ FIX: Check for custom domains FIRST (before production domain check)
  if (hostname !== "localhost" && hostname !== "127.0.0.1") {
    const parts = hostname.split(".");

    if (parts.length >= 2) {
      const firstPart = parts[0];

      if (firstPart !== "www" && firstPart !== "tooraflexonlinestore") {
        const mapped = TENANT_MAPPINGS[firstPart];
        if (mapped) {
          console.log(
            "Found tenant from custom domain:",
            firstPart,
            "->",
            mapped,
          );
          return mapped;
        }
        const domainWithoutTld = parts.slice(0, -1).join(".");
        const mappedFull = TENANT_MAPPINGS[domainWithoutTld];
        if (mappedFull) {
          console.log(
            "Found tenant from full custom domain:",
            domainWithoutTld,
            "->",
            mappedFull,
          );
          return mappedFull;
        }
        if (parts.length > 2) {
          const subdomain = parts[0];
          const domainParts = parts.slice(1);
          const domain = domainParts.join(".");
          for (const [key, value] of Object.entries(TENANT_MAPPINGS)) {
            if (domain.includes(key) || key === domain) {
              console.log("Found tenant from subdomain:", key, "->", value);
              return value;
            }
          }
        }
      }
    }
  }

  // Production: tooraflexonlinestore.co.za domain
  if (hostname.includes("tooraflexonlinestore.co.za")) {
    const parts = hostname.split(".");
    if (parts.length >= 3) {
      const subdomain = parts[0];
      if (subdomain !== "www" && subdomain !== "tooraflexonlinestore") {
        console.log("Found tenant from production subdomain:", subdomain);
        return mapTenantCode(subdomain);
      }
    }
    console.log("Using default tenant for main domain: tooraflex-online");
    return "tooraflex-online";
  }

  // Production: tfonline.co.za domain
  if (hostname.includes(".tfonline.co.za")) {
    const parts = hostname.split(".");
    if (parts.length >= 2) {
      const subdomain = parts[0];
      if (subdomain !== "www") {
        console.log("Found tenant from tfonline subdomain:", subdomain);
        return mapTenantCode(subdomain);
      }
    }
  }

  // Check for other domain patterns
  if (hostname !== "localhost" && hostname !== "127.0.0.1") {
    const parts = hostname.split(".");
    if (parts.length > 2) {
      const subdomain = parts[0];
      if (subdomain !== "www") {
        console.log("Found tenant from generic subdomain:", subdomain);
        return mapTenantCode(subdomain);
      }
    }
  }

  // Default fallback
  console.log("No tenant detected, using default: tooraflex-online");
  return "tooraflex-online";
};

// Get the current tenant code for API headers (synchronous)
export const getCurrentTenantCode = () => {
  const tenantFromUrl = getTenantFromUrl();
  const tenantFromStorage = localStorage.getItem("tenantCode");

  console.log("🔍 getCurrentTenantCode - from URL:", tenantFromUrl);
  console.log("🔍 getCurrentTenantCode - from storage:", tenantFromStorage);

  if (
    tenantFromUrl &&
    tenantFromUrl !== "localhost" &&
    tenantFromUrl !== "null"
  ) {
    return tenantFromUrl;
  }
  if (
    tenantFromStorage &&
    tenantFromStorage !== "null" &&
    tenantFromStorage !== "undefined"
  ) {
    return mapTenantCode(tenantFromStorage);
  }
  return "tooraflex-online";
};

export const getTenantConfig = async (tenantCode) => {
  try {
    const mappedCode = mapTenantCode(tenantCode);
    console.log(`Fetching tenant config for: ${tenantCode} -> ${mappedCode}`);
    const response = await fetch(`/api/tenants/config/${mappedCode}`);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    console.log("Tenant config response:", data);
    return data;
  } catch (error) {
    console.error("Error fetching tenant config:", error);
    throw error;
  }
};

// Utility to check if we're on a tenant subdomain
export const isTenantSubdomain = () => {
  const hostname = window.location.hostname;
  return (
    hostname.includes(".localhost") ||
    hostname.includes(".tfonline.co.za") ||
    hostname.includes(".tooraflexonlinestore.co.za") ||
    (hostname !== "localhost" &&
      hostname !== "127.0.0.1" &&
      hostname.split(".").length > 2)
  );
};

// ✅ FIXED: Get the base API URL for the current tenant
export const getBaseApiUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  // For local development with subdomain (e.g., tenant.localhost)
  if (hostname.includes(".localhost")) {
    const port = 5001;
    return `${protocol}//${hostname}:${port}`;
  }

  // For localhost without subdomain
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    const port = 5001;
    return `${protocol}//localhost:${port}`;
  }

  // ✅ ALWAYS use the current origin for production
  // This ensures lebaba-inware.co.za calls its own API
  // For custom domains like lebaba-inware.co.za
  const parts = hostname.split(".");
  const isCustomDomain =
    parts.length >= 2 &&
    parts[0] !== "www" &&
    parts[0] !== "tooraflexonlinestore" &&
    !hostname.includes("tooraflexonlinestore.co.za") &&
    !hostname.includes("tfonline.co.za");

  if (isCustomDomain) {
    const apiUrl = `${protocol}//${hostname}`;
    console.log(`🔧 Using custom domain for API: ${apiUrl}`);
    return apiUrl;
  }

  // For production - use environment variable or current domain
  if (process.env.REACT_APP_API_URL) {
    let url = process.env.REACT_APP_API_URL;
    if (url.endsWith("/api")) {
      url = url.slice(0, -4);
    }
    console.log(`🔧 Using API URL from env: ${url}`);
    return url;
  }

  // Default: use current origin
  const apiUrl = `${protocol}//${hostname}`;
  console.log(`🔧 Using current origin for API: ${apiUrl}`);
  return apiUrl;
};

// Utility to check if we're on a custom domain
export const isCustomDomain = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");

  return (
    parts.length >= 2 &&
    parts[0] !== "www" &&
    parts[0] !== "tooraflexonlinestore" &&
    !hostname.includes("tooraflexonlinestore.co.za") &&
    !hostname.includes("tfonline.co.za") &&
    !hostname.includes("localhost")
  );
};
